import { jsx } from 'react/jsx-runtime';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { useState, useLayoutEffect } from 'react';
import InjectAside from '../utility/InjectAside.mjs';
import { ModalAside, ModalAsideScrollArea, ModalAsideFooter } from './components/aside/Aside.mjs';
import { ModalBody } from './components/body/Body.mjs';
import { ModalCloseIconButton } from './components/close-icon-button/CloseIconButton.mjs';
import { ModalContent } from './components/content/Content.mjs';
import { ModalDescription } from './components/description/Description.mjs';
import { ModalFog } from './components/fog/Fog.mjs';
import { ModalFooter } from './components/footer/Footer.mjs';
import { ModalHeader } from './components/header/Header.mjs';
import { ModalContent_Legacy as ModalContent$1 } from './components/legacy/Content_Legacy.mjs';
import { ModalFooter_Legacy as ModalFooter$1 } from './components/legacy/Footer_Legacy.mjs';
import { ModalScrollArea_Legacy as ModalScrollArea$1 } from './components/legacy/ScrollArea_Legacy.mjs';
import { ModalPortalContainer } from './components/portal-container/PortalContainer.mjs';
import { ModalScrollArea } from './components/scroll-area/ScrollArea.mjs';
import { ModalSection } from './components/section/Section.mjs';
import { ModalTitle } from './components/title/Title.mjs';
import { ModalToolbar, ModalToolbarTitle } from './components/toolbar/Toolbar.mjs';
import { ModalProvider, useModalContext } from './context.mjs';
import useModalContentElement from './hooks/useModalContentElement.mjs';
import { useModalFooterElement } from './hooks/useModalFooterElement.mjs';

const ModalRoot = (props) => {
  const [scrollPos, setScrollPos] = useState(0);
  const [title, setTitle] = useState(null);
  const [toolbar, setToolbar] = useState(null);
  const [contentElement, setContentElement] = useState(null);
  const [modalContentElement, setModalContentElement] = useState(null);
  const [modalFooterElement, setModalFooterElement] = useState(null);
  const [portalContainerElement, setPortalContainerElement] = useState(void 0);
  const [hasLeftAsideChildren, setHasLeftAsideChildren] = useState(false);
  const [hasRightAsideChildren, setHasRightAsideChildren] = useState(false);
  const {
    children,
    open: openProp,
    defaultOpen,
    onOpenChange,
    onClose,
    ...rootProps
  } = props;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const handleClose = useCallbackRef(onClose);
  return /* @__PURE__ */ jsx(
    ModalProvider,
    {
      modal: rootProps.modal,
      open,
      scrollPos,
      setScrollPos,
      title,
      onTitleChange: setTitle,
      toolbar,
      onToolbarChange: setToolbar,
      contentElement,
      onContentElementChange: setContentElement,
      onClose: handleClose,
      hasLeftAsideChildren,
      onHasLeftAsideChildrenChange: setHasLeftAsideChildren,
      hasRightAsideChildren,
      onHasRightAsideChildrenChange: setHasRightAsideChildren,
      portalContainerElement,
      onPortalContainerElementChange: setPortalContainerElement,
      modalContentElement,
      onChangeModalContentElement: setModalContentElement,
      modalFooterElement,
      onChangeModalFooterElement: setModalFooterElement,
      children: /* @__PURE__ */ jsx(DialogPrimitive.Root, { open, onOpenChange: setOpen, ...rootProps, children })
    }
  );
};
const ModalLeftAsideRoot = ({ children }) => {
  const { onHasLeftAsideChildrenChange } = useModalContext("ModalLeftAsideRoot");
  useLayoutEffect(() => {
    onHasLeftAsideChildrenChange(true);
    return () => {
      onHasLeftAsideChildrenChange(false);
    };
  }, [onHasLeftAsideChildrenChange]);
  return /* @__PURE__ */ jsx(InjectAside, { side: "left", children });
};
const ModalRightAsideRoot = ({
  children
}) => {
  const { onHasRightAsideChildrenChange } = useModalContext(
    "ModalRightAsideRoot"
  );
  useLayoutEffect(() => {
    onHasRightAsideChildrenChange(true);
    return () => {
      onHasRightAsideChildrenChange(false);
    };
  }, [onHasRightAsideChildrenChange]);
  return /* @__PURE__ */ jsx(InjectAside, { side: "right", children });
};
const Modal = ModalRoot;
Modal.Content = ModalContent;
Modal.Content_Legacy = ModalContent$1;
Modal.Trigger = DialogPrimitive.Trigger;
Modal.Close = DialogPrimitive.Close;
Modal.CloseIconButton = ModalCloseIconButton;
Modal.Toolbar = ModalToolbar;
Modal.ToolbarTitle = ModalToolbarTitle;
Modal.Header = ModalHeader;
Modal.Aside = ModalAside;
Modal.AsideScrollArea = ModalAsideScrollArea;
Modal.AsideFooter = ModalAsideFooter;
Modal.LeftAsideRoot = ModalLeftAsideRoot;
Modal.RightAsideRoot = ModalRightAsideRoot;
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
Modal.Section = ModalSection;
Modal.ScrollArea = ModalScrollArea;
Modal.ScrollArea_Legacy = ModalScrollArea$1;
Modal.Fog = ModalFog;
Modal.Footer = ModalFooter;
Modal.Footer_Legacy = ModalFooter$1;
Modal.Body = ModalBody;
Modal.PortalContainer = ModalPortalContainer;
Modal.useModalContentElement = useModalContentElement;
Modal.useModalFooterElement = useModalFooterElement;

export { Modal };
//# sourceMappingURL=Modal.mjs.map
